import axios from "axios";

/**
 * When REACT_APP_API_URL is unset:
 * - In the browser, use the page origin (e.g. http://localhost:3000 or http://192.168.1.5:3000)
 *   so API calls hit the same host as the UI. The CRA dev proxy forwards to the backend.
 * - Outside the browser (tests), fall back to localhost:3001.
 */
const resolveBaseURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://localhost:3001";
};

/** Human-readable message for toasts and boot errors (never hide server JSON behind "Network Error"). */
export function formatApiError(err) {
  const status = err.response?.status;
  const data = err.response?.data;
  const requestUrl = String(err.config?.url || err.request?.responseURL || "").toLowerCase();
  let detail = null;

  if (data && typeof data === "object") {
    detail = data.error ?? data.message;
    if (!detail && Array.isArray(data.errors) && data.errors.length) {
      const first = data.errors[0];
      detail = typeof first === "string" ? first : first?.message || first?.msg;
    }
  }
  if (typeof data === "string" && data.trim()) {
    const text = data.trim();
    const isHtml = /^<!doctype html|^<html[\s>]/i.test(text);
    if (isHtml && (status === 502 || status === 504)) {
      if (requestUrl.includes("/scanner/market")) {
        detail = "Gateway error from nginx. The backend was unavailable or the manual market scan was interrupted before it returned.";
      } else if (requestUrl.includes("/backtest/trade-replay")) {
        detail = "Gateway error from nginx. The backend was unavailable or the trade replay request was interrupted before it returned.";
      } else {
        detail = "Gateway error from nginx. The backend request was interrupted before it returned.";
      }
    } else if (isHtml && status === 405 && requestUrl.includes("/advisor/")) {
      detail = "Nginx is not proxying advisor API routes to the backend. Add /advisor to the API location rule and reload nginx.";
    } else if (isHtml && status === 405 && requestUrl.includes("/trade-blacklist")) {
      detail = "Nginx is not proxying trade-blacklist API routes to the backend. Add /trade-blacklist to the API location rule and reload nginx.";
    } else if (isHtml) {
      detail = "Server returned an HTML error page instead of API JSON.";
    } else {
      detail = text;
    }
  }

  if (!detail && err.code === "ECONNABORTED") detail = "Request timed out";

  if (!err.response) {
    const base = resolveBaseURL();
    const net = err.message || "connection failed";
    if (err.code === "ECONNABORTED") {
      return `API request to ${base} timed out. The backend may still be processing the scan. ${net}`;
    }
    return `Cannot reach API at ${base}. Start the backend on port 3001 (or fix REACT_APP_API_URL / firewall). ${net}`;
  }

  if (!detail) detail = err.message || "Request failed";
  if (status) return `${detail} (HTTP ${status})`;
  return detail;
}

const api = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 20000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export const API_BASE_URL = resolveBaseURL();

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const data = err.response?.data;
    const msg = formatApiError(err);
    const e = new Error(msg);
    e.status = err.response?.status;
    e.needsDashboardLogin = !!(typeof data === "object" && data?.needsDashboardLogin);
    e.raw = err;
    return Promise.reject(e);
  }
);

export const getDashboardMe = () => api.get("/auth/dashboard/me").then((r) => r.data);
export const postDashboardLogin = (body) => api.post("/auth/dashboard/login", body).then((r) => r.data);
export const postDashboardLogout = () => api.post("/auth/dashboard/logout").then((r) => r.data);

export const getBotStatus = () => api.get("/bot/status").then((r) => r.data);
export const getNotificationStatus = () => api.get("/notifications/status").then((r) => r.data);
export const sendTestNotification = () => api.post("/notifications/test", {}, { timeout: 30000 }).then((r) => r.data);
export const getRuntimeResources = (params) => api.get("/runtime/resources", { params, timeout: 60000 }).then((r) => r.data);
export const startBot = () => api.post("/bot/start").then((r) => r.data);
export const stopBot = () => api.post("/bot/stop").then((r) => r.data);
export const runUpstoxAutoLogin = () => api.post("/auth/upstox/auto-login/run", {}, { timeout: 240000 }).then((r) => r.data);
export const getSignals = () => api.get("/signals").then((r) => r.data);
export const getQuote = (symbol, params) =>
  api.get(`/quote/${encodeURIComponent(symbol)}`, { params }).then((r) => r.data);
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const marketScanJobError = (message, status = 500) => {
  const err = new Error(message);
  err.response = { status, data: { error: message } };
  return err;
};

export const runMarketScanner = async (params, onProgress) => {
  const isTransientScanError = (err) => {
    const raw = err?.raw || err;
    const status = raw?.response?.status;
    return raw?.code === "ECONNABORTED" || !raw?.response || status === 502 || status === 503 || status === 504;
  };
  const deadline = Date.now() + 30 * 60 * 1000;
  let missingJobRestarts = 0;

  while (Date.now() < deadline) {
    let created;
    for (let attempt = 0; attempt < 3 && !created; attempt += 1) {
      try {
        created = await api.post("/scanner/market/jobs", params, { timeout: 60000 }).then((r) => r.data);
        if (onProgress) onProgress(created);
      } catch (err) {
        if (!isTransientScanError(err) || attempt === 2) throw err;
        await wait(2000);
      }
    }
    const jobId = created?.jobId;
    if (!jobId) throw marketScanJobError("Backend did not return a market scan job ID.");

    let job = created;
    if (onProgress) onProgress(job);
    let recreateMissingJob = false;
    while (job?.status === "queued" || job?.status === "running") {
      if (Date.now() >= deadline) break;
      await wait(2000);
      try {
        job = await api.get(`/scanner/market/jobs/${encodeURIComponent(jobId)}`, { timeout: 60000 }).then((r) => r.data);
        if (onProgress) onProgress(job);
      } catch (err) {
        const raw = err?.raw || err;
        if (raw?.response?.status === 404 && missingJobRestarts < 2) {
          missingJobRestarts += 1;
          recreateMissingJob = true;
          break;
        }
        if (!isTransientScanError(err)) throw err;
      }
    }
    if (recreateMissingJob) {
      await wait(2000);
      continue;
    }
    if (job?.status === "completed") {
      if (onProgress) onProgress(job);
      return job.result;
    }
    if (job?.status !== "queued" && job?.status !== "running") {
      throw marketScanJobError(job?.error || "Market scan job failed.");
    }
  }
  throw marketScanJobError("Market scan is still running after 30 minutes. Check the backend scanner logs before retrying.", 408);
};
export const getAutoEntryStrategy = () => api.get("/scanner/auto/strategy").then((r) => r.data);
export const getWatchlist = () => api.get("/watchlist").then((r) => r.data);
export const addWatchlistSymbol = (body) => api.post("/watchlist", body, { timeout: 120000 }).then((r) => r.data);
export const removeWatchlistSymbol = (symbol, list) =>
  api.delete(`/watchlist/${encodeURIComponent(symbol)}`, { params: list ? { list } : undefined }).then((r) => r.data);
export const createWatchlist = (name) => api.post("/watchlists", { name }).then((r) => r.data);
export const setActiveWatchlist = (name) => api.post("/watchlists/active", { name }).then((r) => r.data);
export const deleteWatchlist = (name) => api.delete(`/watchlists/${encodeURIComponent(name)}`).then((r) => r.data);
export const getInstrumentSearch = (q) =>
  api.get("/instruments/search", { params: { q }, timeout: 120000 }).then((r) => r.data);
export const getPositions = (params) => api.get("/positions", { params }).then((r) => r.data);
export const getPositionMonitorSymbols = () => api.get("/positions/monitor").then((r) => r.data);
export const addPositionMonitorSymbol = (symbol) =>
  api.post(`/positions/${encodeURIComponent(symbol)}/monitor`).then((r) => r.data);
export const removePositionMonitorSymbol = (symbol) =>
  api.delete(`/positions/${encodeURIComponent(symbol)}/monitor`).then((r) => r.data);
export const getTradeBlacklist = () => api.get("/trade-blacklist").then((r) => r.data);
export const addTradeBlacklistSymbol = (symbol) =>
  api.post("/trade-blacklist", { symbol }).then((r) => r.data);
export const removeTradeBlacklistSymbol = (symbol) =>
  api.delete(`/trade-blacklist/${encodeURIComponent(symbol)}`).then((r) => r.data);
export const getPortfolioSparklines = (body) =>
  api.post("/positions/sparklines", body, { timeout: 120000 }).then((r) => r.data);
export const createPaperPosition = (body) => api.post("/paper/positions", body).then((r) => r.data);
export const exitPosition = (symbol, body = {}) => api.post(`/positions/${encodeURIComponent(symbol)}/exit`, body).then((r) => r.data);
export const getTrades = (params) => api.get("/trades", { params }).then((r) => r.data);
export const getOrders = () => api.get("/orders").then((r) => r.data);
export const getFunds = () => api.get("/funds").then((r) => r.data);
export const getHoldings = () => api.get("/holdings").then((r) => r.data);
export const getCandles = (sym, limit = 80) =>
  api.get(`/candles/${sym}?limit=${limit}`).then((r) => {
    // Handle both array response (legacy) and object response {candles, count, note}
    const data = r.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.candles)) return data.candles;
    return [];
  });
export const runBacktest = (body) => api.post("/backtest", body).then((r) => r.data);
export const runBacktestUpstox = (body) => api.post("/backtest/upstox", body).then((r) => r.data);
export const runTradeReplayBacktest = (body) => api.post("/backtest/trade-replay", body, { timeout: 120000 }).then((r) => r.data);
export const runTradeReplayBatchBacktest = (body) => api.post("/backtest/trade-replay/batch", body, { timeout: 600000 }).then((r) => r.data);
export const prepareTradeReplayBatchDataset = (body) => api.post("/backtest/trade-replay/dataset", body, { timeout: 600000 }).then((r) => r.data);
export const createTradeReplayBatchJob = (body) => api.post("/backtest/trade-replay/batch/jobs", body, { timeout: 120000 }).then((r) => r.data);
export const getTradeReplayBatchJob = (jobId) => api.get(`/backtest/trade-replay/batch/jobs/${encodeURIComponent(jobId)}`, { timeout: 120000 }).then((r) => r.data);
export const getHistoricalCandles = (params) => api.get("/historical-candles", { params }).then((r) => r.data);
export const getAdvisorAnalyses = () => api.get("/advisor/analyses").then((r) => r.data);
export const getAdvisorEntryGateScans = (params) =>
  api.get("/advisor/entry-gate-scans", { params, timeout: 30000 }).then((r) => r.data);
export const exportAdvisorEntryGateScans = (params) =>
  api.get("/advisor/entry-gate-scans/export", { params, timeout: 30000 }).then((r) => r.data);
export const deleteAdvisorEntryGateScan = (id) =>
  api.delete(`/advisor/entry-gate-scans/${encodeURIComponent(id)}`, { timeout: 30000 }).then((r) => r.data);
export const clearAdvisorEntryGateScans = () =>
  api.delete("/advisor/entry-gate-scans", { timeout: 30000 }).then((r) => r.data);
export const getAdvisorAutoMonitorScans = (params) =>
  api.get("/advisor/auto-monitor-scans", { params, timeout: 30000 }).then((r) => r.data);
export const exportAdvisorAutoMonitorScans = (params) =>
  api.get("/advisor/auto-monitor-scans/export", { params, timeout: 30000 }).then((r) => r.data);
export const deleteAdvisorAutoMonitorScan = (id) =>
  api.delete(`/advisor/auto-monitor-scans/${encodeURIComponent(id)}`, { timeout: 30000 }).then((r) => r.data);
export const clearAdvisorAutoMonitorScans = () =>
  api.delete("/advisor/auto-monitor-scans", { timeout: 30000 }).then((r) => r.data);
export const analyzeAdvisorPosition = (symbol) =>
  api.post(`/advisor/positions/${encodeURIComponent(symbol)}/analyze`, {}, { timeout: 180000 }).then((r) => r.data);
export const startAdvisorDeepScan = (mode = "sequential") =>
  api.post("/advisor/positions/deep-scan", { mode }, { timeout: 60000 }).then((r) => r.data);
export const getAdvisorDeepScanStatus = () =>
  api.get("/advisor/deep-scan/status", { timeout: 60000 }).then((r) => r.data);
export const rebalanceAdvisorMonitor = () =>
  api.post("/advisor/monitor/rebalance", {}, { timeout: 180000 }).then((r) => r.data);
export const getAdvisorWorkerUsage = (force = false) =>
  api.get("/advisor/worker-usage", { params: force ? { force: 1 } : undefined, timeout: 20000 }).then((r) => r.data);
export const getAdvisorContextItems = () =>
  api.get("/advisor/context-items", { timeout: 30000 }).then((r) => r.data);
export const getAdvisorContextSelection = () =>
  api.get("/advisor/context-selection", { timeout: 30000 }).then((r) => r.data);
export const putAdvisorContextSelection = (body) =>
  api.put("/advisor/context-selection", body, { timeout: 30000 }).then((r) => r.data);
export const addAdvisorContextItem = (body) =>
  api.post("/advisor/context-items", body, { timeout: 30000 }).then((r) => r.data);
export const addAdvisorSymbolContext = (body) =>
  api.post("/advisor/context-items/symbol", body, { timeout: 120000 }).then((r) => r.data);
export const removeAdvisorContextItem = (id) =>
  api.delete(`/advisor/context-items/${encodeURIComponent(id)}`, { timeout: 30000 }).then((r) => r.data);
export const clearAdvisorContextItems = () =>
  api.delete("/advisor/context-items", { timeout: 30000 }).then((r) => r.data);
export const getAdvisorChatHistory = (params) =>
  api.get("/advisor/chat/history", { params, timeout: 60000 }).then((r) => r.data);
export const postAdvisorChat = (message, contextOptions = {}) =>
  api.post("/advisor/chat", { message, contextOptions }, { timeout: 180000 }).then((r) => r.data);
export const postAdvisorChatStream = async (message, contextOptions = {}, handlers = {}) => {
  const base = resolveBaseURL().replace(/\/$/, "");
  const response = await fetch(`${base}/advisor/chat/stream`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ message, contextOptions }),
  });
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    throw new Error(formatApiError({ response: { status: response.status, data: text }, message: response.statusText, config: { url: "/advisor/chat/stream" } }));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalPayload = null;

  const consumeBlock = (block) => {
    const lines = String(block || "").split(/\r?\n/);
    const event = (lines.find((line) => line.startsWith("event:")) || "event: message").slice(6).trim();
    const dataLines = lines.filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim());
    if (!dataLines.length) return;
    const data = JSON.parse(dataLines.join("\n"));
    if (event === "delta") handlers.onDelta?.(data.text || "");
    if (event === "meta") handlers.onMeta?.(data);
    if (event === "done") finalPayload = data;
    if (event === "error") throw new Error(data.error || "Advisor chat failed");
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const blocks = buffer.split(/\n\n/);
    buffer = blocks.pop() || "";
    for (const block of blocks) consumeBlock(block);
    if (done) break;
  }
  if (buffer.trim()) consumeBlock(buffer);
  return finalPayload || { answer: "" };
};
export const getLoginUrl = () => api.get("/login").then((r) => r.data);
export const logoutServer = () => api.post("/auth/logout").then((r) => r.data);
